import React, {FC, useEffect, useState} from 'react'
import {plannerApiService, PlannerConfigResponse, PlannerConfigLink} from '../services/plannerApi'

interface PlannerConfigModalProps {
  show: boolean
  onClose: () => void
  onSuccess: () => void
}

const PlannerConfigModal: FC<PlannerConfigModalProps> = ({show, onClose, onSuccess}) => {
  const [tenant_id, setTenantId] = useState('')
  const [client_id, setClientId] = useState('')
  const [client_secret, setClientSecret] = useState('')
  const [hasExistingConfig, setHasExistingConfig] = useState(false)
  const [existingLinks, setExistingLinks] = useState<{plan_id: string; project_id: number}[]>([])
  const [loading, setLoading] = useState(false)
  const [initLoading, setInitLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!show) return
    setError(null)
    setLoading(true)
    plannerApiService
      .getConfig()
      .then((configRes: PlannerConfigResponse) => {
        const cfg = configRes.config
        if (cfg) {
          setTenantId(cfg.tenant_id || '')
          setClientId(cfg.client_id || '')
          setClientSecret('')
          setHasExistingConfig(true)
          setExistingLinks(
            (cfg.links || []).map((l: PlannerConfigLink) => ({
              plan_id: l.plan_id || '',
              project_id: l.project_id ?? 0,
            })).filter((l) => l.plan_id.trim() !== '' && l.project_id > 0)
          )
        } else {
          setTenantId('')
          setClientId('')
          setClientSecret('')
          setHasExistingConfig(false)
          setExistingLinks([])
        }
      })
      .catch(() => setError('Erro ao carregar dados'))
      .finally(() => setLoading(false))
  }, [show])

  const handleSaveIntegration = async (e: React.FormEvent) => {
    e.preventDefault()
    const tid = tenant_id.trim()
    const cid = client_id.trim()
    const secret = client_secret.trim()
    if (!tid || !cid) {
      setError('Preencha Tenant ID e Client ID.')
      return
    }
    if (!hasExistingConfig && !secret) {
      setError('Na primeira integração é obrigatório informar o Client Secret.')
      return
    }
    setInitLoading(true)
    setError(null)
    try {
      if (hasExistingConfig) {
        const payload: {
          tenant_id: string
          client_id: string
          client_secret?: string
          links: {plan_id: string; project_id: number}[]
        } = {
          tenant_id: tid,
          client_id: cid,
          links: existingLinks,
        }
        if (secret) payload.client_secret = secret
        await plannerApiService.updateConfig(payload)
        setError(null)
      } else {
        await plannerApiService.init({
          tenant_id: tid,
          client_id: cid,
          client_secret: secret,
        })
        setHasExistingConfig(true)
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Não foi possível salvar a integração')
    } finally {
      setInitLoading(false)
    }
  }

  if (!show) return null

  return (
    <div className='modal fade show d-block' style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
      <div className='modal-dialog modal-dialog-centered modal-lg'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>Gerenciar integração – Microsoft Planner</h5>
            <button type='button' className='btn-close' onClick={onClose} aria-label='Fechar' />
          </div>
          <div className='modal-body'>
            {error && (
              <div className='alert alert-danger py-2' role='alert'>
                {error}
              </div>
            )}

            {loading ? (
              <div className='text-center py-4'>
                <span className='spinner-border spinner-border-sm me-2' /> Carregando...
              </div>
            ) : (
              <>
                {/* Mini tutorial: como obter as informações */}
                <div className='alert alert-info py-3 mb-4' role='alert'>
                  <div className='d-flex align-items-start'>
                    <i className='fas fa-info-circle me-2 mt-1' />
                    <div>
                      <strong className='d-block mb-2'>Como obter as informações:</strong>
                      <ol className='mb-0 ps-3 small'>
                        <li className='mb-1'>
                          Acesse o <a href='https://portal.azure.com' target='_blank' rel='noopener noreferrer'>Portal do Azure</a> e entre com a conta da sua organização Microsoft 365.
                        </li>
                        <li className='mb-1'>
                          Vá em <strong>Azure Active Directory</strong> (ou <strong>Microsoft Entra ID</strong>) → <strong>Registros de aplicativo</strong> → <strong>Novo registro</strong>.
                        </li>
                        <li className='mb-1'>
                          Dê um nome ao app, escolha &quot;Contas em qualquer diretório organizacional&quot; e registre. Anote o <strong>Application (client) ID</strong> → é o <strong>Client ID</strong>.
                        </li>
                        <li className='mb-1'>
                          Na mesma página, em <strong>Diretório (tenant)</strong>, copie o <strong>Directory (tenant) ID</strong> → é o <strong>Tenant ID</strong>.
                        </li>
                        <li className='mb-1'>
                          Em <strong>Certificados e segredos</strong>, crie um <strong>Novo segredo do cliente</strong>. Copie o valor (só é exibido uma vez) → é o <strong>Client Secret</strong>.
                        </li>
                        <li className='mb-1'>
                          Em <strong>Permissões de API</strong>, adicione a permissão <strong>Microsoft Graph</strong> → <strong>Permissões de aplicativo</strong>: <code className='bg-white px-1'>Tasks.ReadWrite.All</code>, <code className='bg-white px-1'>Group.Read.All</code> e <code className='bg-white px-1'>User.Read.All</code>. Conceda o consentimento do administrador.
                        </li>
                        <li>Cole o <strong>Tenant ID</strong>, <strong>Client ID</strong> e <strong>Client Secret</strong> nos campos abaixo.</li>
                      </ol>
                    </div>
                  </div>
                </div>

                {/* Credenciais Azure */}
                <section className='mb-4'>
                  <h6 className='mb-2'>Credenciais do Azure (sua empresa)</h6>
                  <p className='text-muted small mb-3'>
                    Informe <strong>Tenant ID</strong>, <strong>Client ID</strong> e <strong>Client Secret</strong> do registro de aplicativo Microsoft 365. Você pode alterar as credenciais a qualquer momento.
                  </p>
                  <form id='planner-config-form' onSubmit={handleSaveIntegration}>
                    <div className='row g-2 mb-2'>
                      <div className='col-md-4'>
                        <label className='form-label small'>Tenant ID</label>
                        <input
                          type='text'
                          className='form-control form-control-sm'
                          value={tenant_id}
                          onChange={(e) => { setTenantId(e.target.value); setError(null) }}
                          placeholder='Tenant ID'
                        />
                      </div>
                      <div className='col-md-4'>
                        <label className='form-label small'>Client ID</label>
                        <input
                          type='text'
                          className='form-control form-control-sm'
                          value={client_id}
                          onChange={(e) => { setClientId(e.target.value); setError(null) }}
                          placeholder='Client ID'
                        />
                      </div>
                      <div className='col-md-4'>
                        <label className='form-label small'>Client Secret</label>
                        <input
                          type='password'
                          className='form-control form-control-sm'
                          value={client_secret}
                          onChange={(e) => { setClientSecret(e.target.value); setError(null) }}
                          placeholder={hasExistingConfig ? 'Deixe em branco para manter' : 'Client Secret'}
                          autoComplete='off'
                        />
                      </div>
                    </div>
                  </form>
                </section>
              </>
            )}
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-light' onClick={onClose}>
              Fechar
            </button>
            <button
              type='submit'
              form='planner-config-form'
              className='btn btn-primary'
              disabled={
                loading ||
                !tenant_id.trim() ||
                !client_id.trim() ||
                (!hasExistingConfig && !client_secret.trim()) ||
                initLoading
              }
            >
              {initLoading ? <span className='spinner-border spinner-border-sm me-2' /> : null}
              Salvar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PlannerConfigModal
